/**
 * The subscription layer — the same three hooks the Studio has, over `onSnapshot`.
 *
 * Copied rather than shared, and knowingly: the two apps have different Firestore handles
 * (different `db()`, different emulator wiring) and hoisting these would mean hoisting that
 * too. They are forty lines of `onSnapshot` plumbing with no product decisions in them.
 *
 * Live matters here for the same reason it does in the Studio: a pre-render's status has to
 * arrive on its own, because the thing that changes it is a Cloud Function nobody is
 * watching for.
 */

import { MalformedRecordError } from '@atlas-nook/story';
import { onSnapshot } from 'firebase/firestore';
import type { DocumentReference, Query } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';

export interface Subscription<T> {
  data: T;
  /** True until the first snapshot lands. A surface shows its own quiet state, not a spinner. */
  loading: boolean;
  /** A permission refusal, a malformed record, or a dropped connection. */
  error: Error | null;
}

/**
 * A stable key for a ref/query, so a hook does not resubscribe on every render just because
 * the caller built the query inline. Firestore's own `queryEqual` needs both sides, and
 * memoising in every caller is the kind of discipline that quietly rots.
 */
function useStable<T>(value: T, key: string): T {
  const ref = useRef<{ key: string; value: T }>({ key, value });
  if (ref.current.key !== key) ref.current = { key, value };
  return ref.current.value;
}

/** Live one document. `null` data means the document is not there — a fact, not an error. */
export function useDocument<T>(
  reference: DocumentReference<T> | null,
  key: string,
): Subscription<T | null> {
  const [state, setState] = useState<Subscription<T | null>>({
    data: null,
    loading: reference !== null,
    error: null,
  });
  const stable = useStable(reference, reference === null ? 'none' : key);

  useEffect(() => {
    if (stable === null) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState({ data: null, loading: true, error: null });

    return onSnapshot(
      stable,
      (snapshot) => {
        try {
          setState({
            data: snapshot.exists() ? snapshot.data() : null,
            loading: false,
            error: null,
          });
        } catch (error) {
          // A malformed record must not take the surface down; it reads as unopenable.
          setState({ data: null, loading: false, error: asError(error) });
        }
      },
      (error) => setState({ data: null, loading: false, error }),
    );
  }, [stable]);

  return state;
}

/** Live a query. A record that fails to normalize is dropped and reported, not thrown. */
export function useQueryDocs<T>(query: Query<T> | null, key: string): Subscription<T[]> {
  const [state, setState] = useState<Subscription<T[]>>({
    data: [],
    loading: query !== null,
    error: null,
  });
  const stable = useStable(query, query === null ? 'none' : key);

  useEffect(() => {
    if (stable === null) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState({ data: [], loading: true, error: null });

    return onSnapshot(
      stable,
      (snapshot) => {
        const rows: T[] = [];
        let malformed: Error | null = null;
        for (const document of snapshot.docs) {
          try {
            rows.push(document.data());
          } catch (error) {
            if (error instanceof MalformedRecordError) malformed ??= error;
            else throw error;
          }
        }
        setState({ data: rows, loading: false, error: malformed });
      },
      (error) => setState({ data: [], loading: false, error }),
    );
  }, [stable]);

  return state;
}

/** The count of a live query — what the top bar's "N making" and the floor are made of. */
export function useQueryCount(query: Query<unknown> | null, key: string): number {
  const { data } = useQueryDocs(query, key);
  return data.length;
}

function asError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}
